import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { inr, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/dp/search?q=${encodeURIComponent(q)}`);
        setResults(data);
        setOpen(true);
      } catch (e) { /* noop */ }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (id) => {
    setOpen(false); setQ("");
    nav(`/dp/${id}`);
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={ref}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Search DP by name, FE ID, mobile, location…  (Ctrl+K)"
          className="h-10 w-full rounded-lg border border-border bg-secondary/60 pl-9 pr-4 text-sm outline-none transition focus:border-primary focus:bg-background focus:ring-2 focus:ring-primary/20"
        />
      </div>
      {open && results.length > 0 && (
        <div data-testid="global-search-results" className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-2xl">
          {results.map((r) => (
            <button key={r.id} data-testid={`search-result-${r.fe_id}`}
              onMouseDown={() => go(r.id)}
              className="flex w-full items-center gap-3 border-b border-border/60 px-4 py-2.5 text-left transition hover:bg-accent last:border-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">{initials(r.full_name)}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{r.full_name} <span className="text-muted-foreground font-mono text-xs">· {r.fe_id}</span></p>
                <p className="truncate text-xs text-muted-foreground">{r.mobile} · {r.location}</p>
              </div>
              <div className="text-right">
                <StatusBadge status={r.status} />
                <p className="mt-1 text-xs tabular text-muted-foreground">{inr(r.month_earnings)}/mo</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
